import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'kfone-component-library/svgIcons';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IPaginationProps {
  id: string;
  totalItems: number;
  selectedPageSize: number;
  currentPage: number;
  onChange: (page: number, pageSize: number) => void;
  pageSizeOptions: readonly number[];
}

interface IDropdownPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

export const CustomPagination: React.FC<IPaginationProps> = ({
  id,
  totalItems,
  selectedPageSize,
  currentPage,
  onChange,
  pageSizeOptions,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [pageSizeDropdownPosition, setPageSizeDropdownPosition] = useState<IDropdownPosition>({});
  const [pageDropdownPosition, setPageDropdownPosition] = useState<IDropdownPosition>({});

  const pageSizeButtonRef = useRef<HTMLButtonElement>(null);
  const pageButtonRef = useRef<HTMLButtonElement>(null);
  const pageSizeDropdownRef = useRef<HTMLDivElement>(null);
  const pageDropdownRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const totalPages = Math.ceil(totalItems / selectedPageSize);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * selectedPageSize + 1;
  const endItem = Math.min(currentPage * selectedPageSize, totalItems);

  const calculateDropdownPosition = (
    buttonRef: React.RefObject<HTMLButtonElement>,
    dropdownRef: React.RefObject<HTMLDivElement>,
  ): IDropdownPosition => {
    if (!buttonRef.current || !dropdownRef.current) return {};

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = dropdownRef.current.offsetHeight;
    const viewportHeight = window.innerHeight;
    const spaceAbove = buttonRect.top;
    const spaceBelow = viewportHeight - buttonRect.bottom;

    const position: IDropdownPosition = { left: buttonRect.left };

    if (spaceBelow >= dropdownHeight) {
      position.top = buttonRect.bottom;
    } else if (spaceAbove >= dropdownHeight) {
      position.bottom = viewportHeight - (buttonRect.top + 10);
    } else {
      position.top = buttonRect.bottom;
    }

    return position;
  };

  useEffect(() => {
    if (isDropdownOpen && pageSizeButtonRef.current && pageSizeDropdownRef.current) {
      const position = calculateDropdownPosition(pageSizeButtonRef, pageSizeDropdownRef);
      setPageSizeDropdownPosition(position);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isPageDropdownOpen && pageButtonRef.current && pageDropdownRef.current) {
      const position = calculateDropdownPosition(pageButtonRef, pageDropdownRef);
      setPageDropdownPosition(position);
    }
  }, [isPageDropdownOpen]);

  // ✅ Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        isDropdownOpen &&
        pageSizeDropdownRef.current &&
        !pageSizeDropdownRef.current.contains(target) &&
        pageSizeButtonRef.current &&
        !pageSizeButtonRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
      if (
        isPageDropdownOpen &&
        pageDropdownRef.current &&
        !pageDropdownRef.current.contains(target) &&
        pageButtonRef.current &&
        !pageButtonRef.current.contains(target)
      ) {
        setIsPageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, isPageDropdownOpen]);

  const handlePageSizeChange = (newSize: number) => {
    const newTotalPages = Math.ceil(totalItems / newSize);
    const newPage = currentPage > newTotalPages ? newTotalPages : currentPage;
    onChange(newPage ?? 1, newSize);
    setIsDropdownOpen(false);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onChange(newPage, selectedPageSize);
      setIsPageDropdownOpen(false);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      onChange(currentPage - 1, selectedPageSize);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onChange(currentPage + 1, selectedPageSize);
    }
  };

  return (
    <div
      id={id}
      className='flex items-center justify-between border-t border-gray-300 bg-white p-4'
    >
      {/* Left side - Results per page */}
      <div className='flex items-center gap-2'>
        <span className='text-sm text-gray-700'>{t('pe.pagination.resultsPerPage')}</span>
        <div className='relative'>
          <button
            ref={pageSizeButtonRef}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className='flex min-w-[60px] items-center justify-between gap-2 rounded border border-gray-500 bg-white px-2 py-2 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500'
          >
            <span className='text-sm'>{selectedPageSize}</span>
            <ChevronDownIcon className='h-5 w-5 text-gray-600' />
          </button>

          {isDropdownOpen && (
            <div
              ref={pageSizeDropdownRef}
              className='pagination-dropdown pagination-page-size-dropdown absolute z-20 min-w-[60px] -mb-[70px] rounded bg-white shadow-lg'
              style={{
                top: pageSizeDropdownPosition.top,
                bottom: pageSizeDropdownPosition.bottom,
              }}
            >
              {pageSizeOptions.map((size) => (
                <button
                  key={size}
                  onClick={() => handlePageSizeChange(size)}
                  className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                    size === selectedPageSize ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className='text-sm text-gray-600'>
          {t('pe.pagination.showingResults', { start: startItem, end: endItem, total: totalItems })}
        </span>
      </div>

      {/* Right side - Page navigation */}
      <div className='flex items-center gap-4'>
        <div className='relative flex items-center gap-2'>
          <button
            ref={pageButtonRef}
            onClick={() => setIsPageDropdownOpen(!isPageDropdownOpen)}
            className='flex min-w-[60px] items-center justify-between gap-2 rounded border border-gray-500 bg-white p-2 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500'
            disabled={totalPages === 0}
          >
            <span className='text-sm'>{currentPage}</span>
            <ChevronDownIcon className='h-5 w-5 text-gray-600' />
          </button>

          {isPageDropdownOpen && totalPages > 0 && (
            <div
              ref={pageDropdownRef}
              className='pagination-dropdown pagination-page-number-dropdown absolute z-20 max-h-60 min-w-[60px] -mb-[70px] overflow-y-auto rounded bg-white p-2 shadow-lg'
              style={{
                top: pageDropdownPosition.top,
                bottom: pageDropdownPosition.bottom,
              }}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${
                    page === currentPage ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          )}

          <span className='text-sm text-gray-600'>{t('pe.pagination.ofPages', { total: totalPages })}</span>
        </div>

        <div className='flex gap-1'>
          <button
            onClick={handlePrevious}
            disabled={currentPage === 1}
            className='rounded border border-gray-500 p-2 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40'
            aria-label='Previous page'
          >
            <ChevronLeftIcon className='h-5 w-5 text-gray-700' />
          </button>

          <button
            onClick={handleNext}
            disabled={currentPage === totalPages || totalPages === 0}
            className='rounded border border-gray-500 p-2 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40'
            aria-label='Next page'
          >
            <ChevronRightIcon className='h-5 w-5 text-gray-700' />
          </button>
        </div>
      </div>
    </div>
  );
};
